<template>
    <div class="req-page" :dir="$vuetify.rtl ? 'rtl' : 'ltr'">
        <v-card flat class="req-wrap mx-auto rounded-lg" :loading="loading">
            <!-- header -->
            <div class="req-header">
                <v-avatar tile rounded size="44" color="primary" class="rounded-lg me-3">
                    <v-icon dark>mdi-file-document-edit-outline</v-icon>
                </v-avatar>
                <div>
                    <div class="req-title">{{ $t('SurveyRequestFormTitle') }}</div>
                    <div class="req-sub">{{ $t('SurveyRequestFormSubtitle') }}</div>
                </div>
            </div>

            <div v-if="!isPosted" class="pa-4 pa-md-6">
                <v-form ref="reqForm" v-model="valid">
                    <RequestHeaderFields v-model="header" class="mb-5" />

                    <!-- ===== بيانات الجهة ===== -->
                    <v-card outlined class="sec mb-5 rounded-lg">
                        <div class="sec-head"><v-icon small class="me-2">mdi-domain</v-icon>{{ $t('EntityInformation') }}</div>
                        <div class="pa-4">
                            <v-row dense>
                                <v-col cols="12" md="6">
                                    <label class="fld">{{ $t('AgencyOrUnitName') }} <span class="req">*</span></label>
                                    <v-text-field v-model="request.agencyName" outlined dense hide-details="auto" :rules="reqRule" />
                                </v-col>
                                <v-col cols="12" md="6">
                                    <label class="fld">{{ $t('ContactPersonName') }} <span class="req">*</span></label>
                                    <v-text-field v-model="request.contactName" outlined dense hide-details="auto" :rules="reqRule" />
                                </v-col>
                                <v-col cols="12" md="6">
                                    <label class="fld">{{ $t('JobTitle') }} <span class="req">*</span></label>
                                    <v-text-field v-model="request.jobTitle" outlined dense hide-details="auto" :rules="reqRule" />
                                </v-col>
                                <v-col cols="12" md="3">
                                    <label class="fld">{{ $t('Mobile') }} <span class="req">*</span></label>
                                    <v-text-field v-model="request.mobile" outlined dense hide-details="auto" :rules="phoneRule"
                                                  prepend-inner-icon="mdi-phone" />
                                </v-col>
                                <v-col cols="12" md="3">
                                    <label class="fld">{{ $t('Email') }} <span class="req">*</span></label>
                                    <v-text-field v-model="request.email" outlined dense hide-details="auto" :rules="emailRule"
                                                  prepend-inner-icon="mdi-email-outline" />
                                </v-col>
                            </v-row>
                        </div>
                    </v-card>

                    <!-- ===== بيانات الاستطلاع ===== -->
                    <v-card outlined class="sec mb-5 rounded-lg">
                        <div class="sec-head"><v-icon small class="me-2">mdi-clipboard-text-outline</v-icon>{{ $t('SurveyInformation') }}</div>
                        <div class="pa-4">
                            <v-row dense>
                                <v-col cols="12" md="8">
                                    <label class="fld">{{ $t('SurveyTitleField') }} <span class="req">*</span></label>
                                    <v-text-field v-model="request.surveyTitle" outlined dense hide-details="auto" :rules="reqRule" />
                                </v-col>
                                <v-col cols="12" md="4">
                                    <label class="fld">{{ $t('SurveyStatus') }} <span class="req">*</span></label>
                                    <v-radio-group v-model="request.surveyStatus" row :rules="reqRule" hide-details="auto" class="mt-1">
                                        <v-radio :label="$t('StatusNew')" value="new" />
                                        <v-radio :label="$t('StatusPreviouslyConducted')" value="previous" />
                                    </v-radio-group>
                                </v-col>

                                <v-col cols="12">
                                    <label class="fld">{{ $t('SurveyObjectives') }} <span class="req">*</span></label>
                                    <div class="hint">{{ $t('SurveyObjectivesHint') }}</div>
                                    <v-textarea v-model="request.objectives" outlined dense rows="3" hide-details="auto" :rules="reqRule" auto-grow />
                                </v-col>

                                <v-col cols="12">
                                    <label class="fld">{{ $t('SurveyType') }} <span class="req">*</span></label>
                                    <div class="chk-group">
                                        <v-checkbox v-model="request.surveyTypes" value="kpis" hide-details dense :label="$t('SurveyTypeKpis')" class="mt-0" />
                                        <v-checkbox v-model="request.surveyTypes" value="minister" hide-details dense
                                                    :label="$t('SurveyTypeMinisterFull')" />
                                        <v-expand-transition>
                                            <v-file-input v-if="request.surveyTypes.includes('minister')" v-model="ministerFile"
                                                          outlined dense hide-details="auto" class="mt-3" :label="$t('AttachMeetingMinutes')"
                                                          prepend-icon="" prepend-inner-icon="mdi-paperclip" />
                                        </v-expand-transition>
                                    </div>
                                </v-col>

                                <v-col cols="12">
                                    <label class="fld">{{ $t('SurveyImportanceImpact') }} <span class="req">*</span></label>
                                    <v-textarea v-model="request.importanceImpact" outlined dense rows="4" hide-details="auto" :rules="reqRule" auto-grow />
                                </v-col>

                                <v-col cols="12">
                                    <label class="fld">{{ $t('QuestionThemes') }} <span class="req">*</span></label>
                                    <div class="hint">{{ $t('QuestionThemesHint') }}</div>
                                    <v-textarea v-model="request.questionThemes" outlined dense rows="3" hide-details="auto" :rules="reqRule" auto-grow />
                                </v-col>
                            </v-row>
                        </div>
                    </v-card>

                    <!-- ===== الفئة المستهدفة من الاستطلاع ===== -->
                    <!-- Gates everything below it: general/ministry go straight through, "other" asks
                         for a description + whether sample contact data exists. "no" blocks submission. -->
                    <v-card outlined class="sec mb-5 rounded-lg">
                        <div class="sec-head"><v-icon small class="me-2">mdi-account-search-outline</v-icon>{{ $t('SurveyTargetAudience') }} <span class="req ms-1">*</span></div>
                        <div class="pa-4">
                            <div class="chk-group">
                                <v-radio-group v-model="request.sampleScope" :rules="reqRule" hide-details="auto" class="mt-0">
                                    <v-radio :label="$t('SampleScopeGeneral')" value="general" />
                                    <v-radio :label="$t('SampleScopeMinistry')" value="ministry" />
                                    <v-radio :label="$t('SampleScopeOther')" value="other" />
                                </v-radio-group>
                            </div>

                            <v-expand-transition>
                                <div v-if="isOtherScope" class="mt-4">
                                    <label class="fld">{{ $t('DescribeTargetAudience') }} <span class="req">*</span></label>
                                    <v-textarea v-model="request.otherSampleDescription" outlined dense rows="3" hide-details="auto"
                                                :rules="isOtherScope ? reqRule : []" auto-grow />

                                    <label class="fld mt-4">{{ $t('HasSampleContactsQuestion') }} <span class="req">*</span></label>
                                    <div class="chk-group">
                                        <v-radio-group v-model="request.hasSampleContacts" row :rules="isOtherScope ? reqRule : []"
                                                       hide-details="auto" class="mt-0">
                                            <v-radio :label="$t('Yes')" value="yes" />
                                            <v-radio :label="$t('No')" value="no" />
                                        </v-radio-group>
                                    </div>
                                </div>
                            </v-expand-transition>
                        </div>
                    </v-card>

                    <!-- no sample contact data -> the request cannot be submitted -->
                    <v-expand-transition>
                        <div v-if="isBlocked" class="blocked mb-5">
                            <v-icon class="blocked-ic me-2">mdi-alert-circle-outline</v-icon>
                            <!-- three sentences rather than one string: Dictionary.AR/EN are nvarchar(200) -->
                            <div class="blocked-txt">
                                <p class="mb-1">{{ $t('SampleUnavailable1') }}</p>
                                <p class="mb-1">{{ $t('SampleUnavailable2') }}</p>
                                <p class="mb-0">{{ $t('SampleUnavailable3') }}</p>
                            </div>
                        </div>
                    </v-expand-transition>

                    <template v-if="showRemainder">
                        <!-- ===== تحديد نوع العينة المستهدفة من الاستطلاع ===== -->
                        <v-card outlined class="sec mb-5 rounded-lg">
                            <div class="sec-head"><v-icon small class="me-2">mdi-account-group-outline</v-icon>{{ $t('TargetSampleType') }} <span class="req ms-1">*</span></div>
                            <div class="pa-4">
                                <div class="chk-group">
                                    <v-row dense>
                                        <v-col cols="6" sm="4" md="3" v-for="d in demographicsList" :key="d.value">
                                            <v-checkbox v-model="request.demographics" :value="d.value" :label="d.label" hide-details dense class="mt-0" />
                                        </v-col>
                                    </v-row>
                                </div>
                                <v-expand-transition>
                                    <v-text-field v-if="request.demographics.includes('other')" v-model="request.demographicsOther"
                                                  outlined dense hide-details="auto" class="mt-3" :label="$t('OtherDataSpecify')" />
                                </v-expand-transition>
                            </div>
                        </v-card>

                        <!-- ===== قاعدة البيانات ===== -->
                        <v-card outlined class="sec mb-5 rounded-lg">
                            <div class="sec-head"><v-icon small class="me-2">mdi-database-outline</v-icon>{{ $t('DatabaseSection') }}</div>
                            <div class="pa-4">
                                <div class="chk-group">
                                    <v-checkbox v-model="request.dataAvailability" value="available" hide-details dense class="mt-0"
                                                :label="$t('DataAvailableAtRequesterFull')" />
                                    <v-checkbox v-model="request.dataAvailability" value="other" hide-details dense :label="$t('OtherSpecify')" />
                                </div>
                                <v-expand-transition>
                                    <v-text-field v-if="request.dataAvailability.includes('other')" v-model="request.dataAvailabilityOther"
                                                  outlined dense hide-details="auto" class="mt-3" :label="$t('OtherSpecify')" />
                                </v-expand-transition>
                            </div>
                        </v-card>

                        <!-- ===== مرفقات ===== -->
                        <v-card outlined class="sec mb-5 rounded-lg">
                            <div class="sec-head"><v-icon small class="me-2">mdi-paperclip</v-icon>{{ $t('Attachments') }}</div>
                            <div class="pa-4">
                                <label class="fld">{{ $t('AttachRequestFileOptional') }}</label>
                                <div class="hint">{{ $t('AttachFileHint') }}</div>
                                <v-file-input :value="attachmentFiles" outlined dense hide-details="auto" multiple counter
                                              accept=".doc,.docx,.pdf,.xls,.xlsx" prepend-icon="" prepend-inner-icon="mdi-file-upload-outline"
                                              :label="$t('ChooseWordPdfExcelFile')" :rules="filesRule" clearable
                                              @change="addAttachmentFiles">
                                    <template v-slot:selection="{ index }">
                                        <v-chip small close class="me-1 mb-1" @click:close="attachmentFiles.splice(index, 1)">
                                            {{ attachmentFiles[index] ? attachmentFiles[index].name : '' }}
                                        </v-chip>
                                    </template>
                                </v-file-input>
                            </div>
                        </v-card>

                        <!-- ===== الأسئلة المقترحة ===== -->
                        <v-card outlined class="sec mb-2 rounded-lg">
                            <div class="sec-head">
                                <v-icon small class="me-2">mdi-format-list-numbered</v-icon>
                                <span>{{ $t('ProposedSurveyQuestions') }}</span>
                                <v-spacer />
                                <span class="q-count">{{ request.proposedQuestions.length }} / 15</span>
                            </div>
                            <div class="pa-4 q-list">
                                <div v-for="(q, i) in request.proposedQuestions" :key="i" class="q-card">
                                    <!-- card header: number, title, "has options" toggle, remove -->
                                    <div class="q-head d-flex align-center">
                                        <div class="q-num me-3">{{ i + 1 }}</div>
                                        <span class="q-title">{{ $t('Question') }} {{ i + 1 }}</span>
                                        <v-spacer />
                                        <v-checkbox v-model="q.hasOptions" dense hide-details class="mt-0 pt-0 q-has-opts"
                                                    :label="$t('QuestionHasOptions')" @change="onHasOptionsChange(q)" />
                                        <v-tooltip bottom v-if="request.proposedQuestions.length > 1">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-btn icon small color="error" class="ms-2" v-bind="attrs" v-on="on" @click="removeQuestion(i)">
                                                    <v-icon small>mdi-trash-can-outline</v-icon>
                                                </v-btn>
                                            </template>
                                            <span>{{ $t('Delete') }}</span>
                                        </v-tooltip>
                                    </div>
                                    <div class="q-body">
                                        <!-- question text editor -->
                                        <div class="q-block">
                                            <div class="q-block-label">
                                                <v-icon x-small class="me-1">mdi-text-box-edit-outline</v-icon>
                                                <span>{{ $t('ProposedQuestionPlaceholder') }}</span>
                                            </div>
                                            <div class="q-editor">
                                                <TextEditor v-model="q.text" :editable="!btnLoading" max-height="9rem" />
                                            </div>
                                        </div>
                                        <!-- answer options editor (one option per line) -->
                                        <v-expand-transition>
                                            <div v-if="q.hasOptions" class="q-block q-block--opts">
                                                <div class="q-block-label">
                                                    <v-icon x-small class="me-1">mdi-format-list-bulleted</v-icon>
                                                    <span>{{ $t('AnswerOptions') }}</span>
                                                    <span class="q-hint ms-2">{{ $t('OneOptionPerLine') }}</span>
                                                </div>
                                                <div class="q-editor">
                                                    <TextEditor v-model="q.optionsHtml" :editable="!btnLoading" max-height="9rem" />
                                                </div>
                                            </div>
                                        </v-expand-transition>
                                    </div>
                                </div>
                                <v-btn outlined block color="primary" class="q-add" @click="addQuestion" :disabled="request.proposedQuestions.length >= 15">
                                    <v-icon small class="me-1">mdi-plus-circle-outline</v-icon> {{ $t('AddQuestion') }}
                                </v-btn>
                            </div>
                        </v-card>
                    </template>
                </v-form>
            </div>

            <div v-else class="text-center py-12">
                <v-icon size="72" color="success">mdi-check-circle-outline</v-icon>
                <div class="text-h6 mt-3">{{ $t('RequestSubmittedAndStarted') }}</div>
                <div class="grey--text mt-1" v-if="referenceNumber">{{ $t('RequestNumberLabel') }} {{ referenceNumber }}</div>
            </div>

            <!-- hidden until a sample scope is chosen, and while the request is blocked -->
            <template v-if="!isPosted && showRemainder">
                <v-divider />
                <div class="d-flex justify-end pa-4">
                    <v-btn color="primary" depressed large :loading="btnLoading" @click="submit">
                        <v-icon class="me-1">mdi-send</v-icon> {{ $t('SubmitRequest') }}
                    </v-btn>
                </div>
            </template>
        </v-card>
    </div>
</template>

<script>
    import ProcessesService from '@/services/ProcessesService.js'
    import RequestHeaderFields, { emptyRequestHeader } from '@/components/app/process/RequestHeaderFields.vue'
    import TextEditor from '@/components/controls/TextEditor.vue'
    import { mapGetters } from 'vuex'

    export default {
        components: { RequestHeaderFields, TextEditor },

        data() {
            return {
                header: emptyRequestHeader(),
                loading: false,
                btnLoading: false,
                valid: true,
                isPosted: false,
                referenceNumber: '',
                ministerFile: null,
                attachmentFiles: [],
                reqRule: [v => (!!v && String(v).trim().length > 0) || this.$t('Required')],
                emailRule: [
                    v => (!!v && String(v).trim().length > 0) || this.$t('Required'),
                    v => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v || '') || this.$t('InvalidEmail'),
                ],
                phoneRule: [
                    v => (!!v && String(v).trim().length > 0) || this.$t('Required'),
                    v => /^(?:\+?966|00966|0)?5\d{8}$/.test(this.normalizeDigits(v).replace(/[\s\-()]/g, '')) || this.$t('InvalidMobile'),
                ],
                fileRule: [
                    v => !v || /\.(doc|docx|pdf|xls|xlsx)$/i.test(v.name) || this.$t('FileMustBeWordPdfExcel'),
                ],
                filesRule: [
                    v => !v || !v.length || v.every(f => /\.(doc|docx|pdf|xls|xlsx)$/i.test(f.name)) || this.$t('FileMustBeWordPdfExcel'),
                ],
                demographicsList: [
                    { value: 'gender', label: this.$t('Gender') },
                    { value: 'housing', label: this.$t('HousingType') },
                    { value: 'age', label: this.$t('AgeGroup') },
                    { value: 'activity', label: this.$t('ActivityType') },
                    { value: 'education', label: this.$t('EducationalLevel') },
                    { value: 'activityYears', label: this.$t('ActivityYears') },
                    { value: 'municipality', label: this.$t('MunicipalityName') },
                    { value: 'other', label: this.$t('OtherDataSpecify') },
                ],
                request: {
                    agencyName: '', contactName: '', jobTitle: '', mobile: '', email: '',
                    surveyTitle: '', surveyStatus: '', objectives: '',
                    surveyTypes: [],
                    importanceImpact: '',
                    questionThemes: '',
                    sampleScope: '', otherSampleDescription: '', hasSampleContacts: '',
                    targetSampleDetails: '', demographics: [], demographicsOther: '',
                    dataAvailability: [], dataAvailabilityOther: '',
                    proposedQuestions: [{ text: '', hasOptions: false, optionsHtml: '' }],
                },
            }
        },
        computed: {
            ...mapGetters('user', ['structureId']),
            isOtherScope() {
                return this.request.sampleScope === 'other'
            },
            // "عينة أخرى" + no sample contact data -> request cannot be submitted
            isBlocked() {
                return this.isOtherScope && this.request.hasSampleContacts === 'no'
            },
            // Everything from "تحديد نوع العينة" down to the submit button.
            // v-if (not v-show) so hidden inputs unregister from the form and their rules stop applying.
            showRemainder() {
                const scope = this.request.sampleScope
                if (scope === 'general' || scope === 'ministry') return true
                if (scope === 'other') return this.request.hasSampleContacts === 'yes'
                return false
            },
        },
        watch: {
            // leaving "عينة أخرى" clears its follow-up answers so they can't be
            // submitted alongside a different scope
            'request.sampleScope'(scope) {
                if (scope !== 'other') {
                    this.request.otherSampleDescription = ''
                    this.request.hasSampleContacts = ''
                }
            },
        },
        methods: {
            // convert Arabic-Indic (٠-٩) and Persian (۰-۹) digits to ASCII 0-9
            stripToText(html) {
                return String(html || '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').trim()
            },
            // editor HTML -> one option per paragraph / list item / line break, as plain text
            optionsFromHtml(html) {
                return String(html || '')
                    .replace(/<\/(p|li|div|h[1-6]|tr)>/gi, '\n')
                    .replace(/<br\s*\/?>/gi, '\n')
                    .replace(/<[^>]*>/g, '')
                    .replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"')
                    .split('\n').map(o => o.trim()).filter(o => o.length > 0)
            },
            addQuestion() {
                if (this.request.proposedQuestions.length < 15) this.request.proposedQuestions.push({ text: '', hasOptions: false, optionsHtml: '' })
            },
            removeQuestion(i) {
                this.request.proposedQuestions.splice(i, 1)
            },
            onHasOptionsChange(q) {
                if (!q.hasOptions) q.optionsHtml = ''
            },
            normalizeDigits(s) {
                return String(s || '')
                    .replace(/[٠-٩]/g, d => '٠١٢٣٤٥٦٧٨٩'.indexOf(d))
                    .replace(/[۰-۹]/g, d => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d))
            },
            // v-file-input replaces its value on every pick; merge instead so users can
            // add attachments across several picks. An empty emit = the clear icon.
            addAttachmentFiles(selected) {
                if (!selected || selected.length === 0) {
                    this.attachmentFiles = []
                    return
                }
                const merged = [...this.attachmentFiles]
                for (const f of selected) {
                    if (!merged.some(x => x.name === f.name && x.size === f.size && x.lastModified === f.lastModified)) {
                        merged.push(f)
                    }
                }
                this.attachmentFiles = merged
            },
            submit() {
                // guards the blocked case even if the button is somehow reachable
                if (!this.showRemainder) {
                    return
                }
                if (!this.$refs.reqForm.validate()) {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                    return
                }
                this.btnLoading = true
                const payload = {
                    ...this.header,
                    ...this.request,
                    mobile: this.normalizeDigits(this.request.mobile).replace(/[\s\-()]/g, ''),
                    proposedQuestions: this.request.proposedQuestions
                        .filter(q => q && this.stripToText(q.text).length > 0)
                        .map(q => ({
                            text: q.text,                                   // HTML from the editor
                            options: q.hasOptions ? this.optionsFromHtml(q.optionsHtml) : [],
                        })),
                    structureId: this.structureId,
                }
                const formData = new FormData()
                formData.append('objectData', JSON.stringify(payload))
                if (this.ministerFile) formData.append('files', this.ministerFile)
                for (const f of this.attachmentFiles || []) formData.append('files', f)

                ProcessesService.createSurveyExecutionRequest(formData).then((res) => {
                    if (res.data && res.data.success === false) {
                        this.$root.$notification(this.$t(res.data.message), 'error')
                    } else {
                        this.referenceNumber = res.data ? res.data.referenceNumber : ''
                        this.isPosted = true
                        this.$root.$notification(this.$t('WorkflowStarted'), 'success')
                    }
                }).catch(error => {
                    // Prefer the server's own keyword (e.g. NoPermissionToCreateProcess on a 403) so the
                    // user is told *why* it failed. A network failure rejects with no `response` at all,
                    // hence the guards; $te() keeps an untranslated keyword from rendering raw.
                    const key = error && error.response && error.response.data
                        ? error.response.data.message
                        : null
                    const text = key && this.$te(key) ? this.$t(key) : this.$t('ErrorOccurred')
                    this.$root.$notification(text, 'error')
                }).finally(() => {
                    this.btnLoading = false
                })
            },
        },
    }
</script>

<style scoped>
    .req-page {
        background: #f4f6f8;
        padding: 18px;
        direction: rtl;
        text-align: right;
    }
    .req-wrap {
        max-width: 1080px;
    }
    .req-header {
        display: flex;
        align-items: center;
        padding: 18px 22px;
        border-bottom: 1px solid #eceff1;
    }
    .req-title { font-size: 18px; font-weight: 700; line-height: 1.3; }
    .req-sub { font-size: 12.5px; color: #90a4ae; }

    .sec { overflow: hidden; border-color: #e3e6ea !important; }
    .sec-head {
        display: flex;
        align-items: center;
        font-weight: 700;
        font-size: 14px;
        padding: 10px 16px;
        background: #f1f7f4;
        color: var(--v-primary-base, #1B8354);
        border-bottom: 1px solid #e3e6ea;
    }
    .fld { font-size: 13px; font-weight: 600; display: block; margin-bottom: 4px; color: #37474f; }
    .req { color: #e53935; }
    .hint { font-size: 12px; color: #9aa3ab; margin-bottom: 6px; }

    .chk-group {
        border: 1px solid #e3e6ea;
        border-radius: 8px;
        padding: 6px 14px;
        background: #fafbfc;
    }

    /* blocking notice shown when the target sample's contact data isn't available */
    .blocked {
        display: flex;
        align-items: flex-start;
        border: 1px solid #f0cdc8;
        border-radius: 10px;
        padding: 14px 16px;
        background: #fdf5f4;
    }
    .blocked-ic { color: #c0392b !important; margin-top: 1px; flex: 0 0 auto; }
    .blocked-txt { font-size: 13px; font-weight: 600; color: #8f3325; line-height: 1.95; }

    /* ---- proposed questions: one card per question, two editors inside ---- */
    .q-count {
        font-size: 12px; font-weight: 700; color: #1B8354;
        background: #e6f2ec; border-radius: 12px; padding: 2px 10px;
    }
    .q-list { display: flex; flex-direction: column; gap: 14px; }
    .q-card {
        border: 1px solid #e1e7ec;
        border-radius: 10px;
        background: #fff;
        overflow: hidden;
        box-shadow: 0 1px 3px rgba(20, 40, 60, 0.05);
        transition: box-shadow .15s ease, border-color .15s ease;
    }
    .q-card:hover { border-color: #b9d6c6; box-shadow: 0 3px 10px rgba(27, 131, 84, 0.10); }
    .q-head {
        background: #f4f7f9;
        border-bottom: 1px solid #e6ebf0;
        padding: 8px 12px;
        min-height: 46px;
    }
    .q-num {
        min-width: 26px; height: 26px; border-radius: 50%;
        background: var(--v-primary-base, #1B8354); color: #fff;
        display: flex; align-items: center; justify-content: center;
        font-size: 12px; font-weight: 700;
    }
    .q-title { font-size: 13.5px; font-weight: 700; color: #37474f; }
    .q-has-opts { flex: 0 0 auto; }
    .q-has-opts >>> label { font-size: 12.5px; font-weight: 600; color: #455a64; }
    .q-body { padding: 12px 14px 14px; display: flex; flex-direction: column; gap: 12px; }
    .q-block-label {
        display: flex; align-items: center;
        font-size: 12.5px; font-weight: 700; color: #546e7a;
        margin-bottom: 6px;
    }
    .q-block-label .v-icon { color: #1B8354 !important; }
    .q-hint { font-size: 11.5px; font-weight: 500; color: #90a4ae; }
    .q-block--opts {
        border-inline-start: 3px solid #cfe7da;
        padding-inline-start: 10px;
    }
    /* compact editors: the shared TextEditor defaults to a fixed 20em body and a heavy 2px border */
    .q-editor >>> .v-card { border: 1px solid #dfe5ea !important; border-radius: 8px !important; overflow: hidden; }
    .q-editor >>> .v-card__title { min-height: 36px; padding: 0 4px !important; }
    .q-editor >>> .tiptap { border: 0 !important; height: auto !important; min-height: 5em; max-height: 9rem; overflow: auto; }
    .q-editor >>> .ProseMirror { padding: 10px 14px; min-height: 5em; font-size: 13.5px; line-height: 1.8; outline: none; }
    .q-add {
        border-style: dashed !important;
        text-transform: none; font-weight: 700; letter-spacing: 0;
        height: 40px !important;
    }

</style>
